import { useMemo } from 'react';

import { Box, Chip, Stack, Tooltip, Typography, styled } from '@mui/joy';
import { IoMdLock } from 'react-icons/io';
import { TbLockOpen } from 'react-icons/tb';
import { Link } from 'react-router-dom';

import { usePermissionsContext } from '../../../context/permissionsContext';
import useFetchAllArticlesByCategory from '../../../services/strapi/useFetchAllArticlesByCategory';
import { Article } from '../../../types/strapi';
import { UPGRADE_SOURCE } from '../../../utils/constants/upgradeSources';
import { dateFormat } from '../../../utils/functions/date';
import { upgradeLinkProps } from '../../../utils/functions/upgradeLink';
import { usePermissions } from '../../../utils/hooks/usePermissions';

export const Highlighted = styled(Box)(({ theme }) => ({
  display: 'inline',
  '& mark': {
    backgroundColor: theme.palette.primary.outlinedHoverBg,
    color: 'inherit',
    borderRadius: '2px',
    padding: '0 1px',
  },
}));

// Article covers aren't in the Typesense index, so resolve them from the cached
// Strapi article list (same query key as the hub — deduped by react-query).
export const useArticleCovers = () => {
  const { hasPermission } = usePermissions();
  const { isCompassEnabled, whitelistedProjectTypes } = usePermissionsContext();
  const { data } = useFetchAllArticlesByCategory(
    { category: null, projectTypes: isCompassEnabled ? whitelistedProjectTypes : [] },
    hasPermission('research_hub:research_list')
  );
  return useMemo(() => {
    const map = new Map<string, string>();
    (data as Article[] | undefined)?.forEach((a) => {
      if (a?.url && a?.cover?.url) map.set(a.url, a.cover.url);
    });
    return map;
  }, [data]);
};

interface Props {
  hit: any;
  coverByUrl?: Map<string, string>;
}

export const SearchResultCard = ({ hit, coverByUrl }: Props) => {
  const { hasPermission } = usePermissions();
  const doc = hit?.document ?? {};
  const isArticle = doc.type === 'article';
  const isTopicPage = doc.type === 'topic_page';
  const hasSubscriberOnlyAccess = hasPermission('research_hub:subscriber_only');
  const isLocked = (doc.is_subscriber_only || !isArticle) && !hasSubscriberOnlyAccess;

  const redirectionLink = isTopicPage
    ? doc.topic_to
    : isArticle
      ? `/research-hub/${doc.category}/${doc.url}`
      : `/research-hub/research_update/${doc.date}`;
  const linkProps = isLocked
    ? upgradeLinkProps(UPGRADE_SOURCE.INSIGHTS_HUB_SEARCH_LOCKED_RESULT)
    : {
        to: doc.content_detail_alternate_url ?? redirectionLink,
        target: doc.content_detail_alternate_url ? ('_blank' as const) : ('_self' as const),
      };

  const cover = coverByUrl?.get(doc.url) ?? doc.cover_url ?? doc.cover?.url ?? doc.image ?? '';
  const title =
    isArticle || isTopicPage ? doc.title : `Research update - ${dateFormat(doc.date).monthddyyyy}`;
  const titleHtml = hit?.highlight?.title?.snippet;
  const snippetHtml =
    hit?.highlight?.content?.snippet ||
    hit?.highlight?.summary?.snippet ||
    hit?.highlight?.preview?.snippet;
  const snippet = doc[isArticle ? 'content' : 'summary'];
  // Research updates aren't indexed with a category, so label them explicitly.
  const category =
    isArticle || isTopicPage ? doc.category_display_name ?? doc.category : 'Research team updates';
  const date = doc.published_date ?? doc.date;

  return (
    <Tooltip title={isLocked ? 'Upgrade for access' : ''} placement='top'>
      <Box sx={{ position: 'relative' }}>
        <Link
          {...linkProps}
          aria-label={title}
          style={{ position: 'absolute', inset: 0, zIndex: 1, textDecoration: 'none' }}
        />
        <Stack
          direction='row'
          gap={1}
          sx={{ borderRadius: 'md', overflow: 'hidden', '&:hover': { opacity: 0.9 } }}
        >
          <Box
            sx={(theme) => ({
              flexShrink: 0,
              width: 200,
              height: 110,
              borderRadius: 'md',
              backgroundColor: theme.palette.background.level2,
              backgroundImage: cover ? `url(${cover})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            })}
          >
            {isTopicPage && doc.image_label && (
              <Box
                sx={{
                  width: 100,
                  height: 55,
                  borderRadius: 'md',
                  backgroundColor: 'background.transparency',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={(theme) => ({ fontSize: '16px', color: theme.palette.common.white })}
                >
                  {doc.image_label}
                </Typography>
              </Box>
            )}
          </Box>
          <Stack gap={isTopicPage ? 0.5 : 1} justifyContent='center' sx={{ flex: 1, minWidth: 0 }}>
            {isTopicPage && category && (
              <Chip
                endDecorator={
                  hasSubscriberOnlyAccess ? (
                    <TbLockOpen size={18} style={{ transform: 'rotateY(180deg)' }} />
                  ) : (
                    <IoMdLock size={18} />
                  )
                }
                sx={(theme) => ({
                  alignSelf: 'flex-start',
                  borderRadius: '24px',
                  minHeight: 24,
                  backgroundColor: theme.palette['expert']['light'],
                  color: theme.palette.common.white,
                  fontSize: 'sm',
                  fontWeight: 'md',
                  '--Chip-paddingInline': '8px',
                  '--Chip-gap': '6px',
                })}
              >
                {category}
              </Chip>
            )}
            <Typography
              sx={(theme) => ({
                fontSize: '16px',
                fontWeight: 700,
                color: theme.palette.text.primary,
              })}
            >
              {titleHtml ? <Highlighted dangerouslySetInnerHTML={{ __html: titleHtml }} /> : title}
            </Typography>
            {(snippetHtml || snippet) && (
              <Typography
                sx={(theme) => ({
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: 1.33,
                  color: theme.palette.text.primary,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                })}
              >
                {snippetHtml ? (
                  <Highlighted dangerouslySetInnerHTML={{ __html: snippetHtml }} />
                ) : (
                  snippet
                )}
              </Typography>
            )}
            <Stack direction='row' alignItems='center' gap={1}>
              {!isTopicPage && category && (
                <Chip
                  variant='outlined'
                  size='sm'
                  component='a'
                  href={`/research-hub/search?q=${encodeURIComponent(category)}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  title={`Search for ${category}`}
                  sx={(theme) => ({
                    position: 'relative',
                    zIndex: 2,
                    borderRadius: '24px',
                    backgroundColor: theme.palette.background.surface,
                    borderColor: theme.palette.border.border,
                    color: theme.palette.text.primary,
                    fontSize: '12px',
                    fontWeight: 500,
                    textDecoration: 'none',
                    '--Chip-minHeight': '20px',
                    '--Chip-paddingInline': '6px',
                    '&:hover': { backgroundColor: theme.palette.primary.outlinedHoverBg },
                  })}
                >
                  {category}
                </Chip>
              )}
              {date && (
                <Typography
                  sx={(theme) => ({
                    fontSize: '14px',
                    fontWeight: 700,
                    color: theme.palette.text.secondary,
                  })}
                >
                  {dateFormat(date).monthddyyyy}
                </Typography>
              )}
              {!isTopicPage && isLocked && <IoMdLock size={16} />}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Tooltip>
  );
};

export default SearchResultCard;
